import React, { useState, useRef, useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./VideoGallery.css";
import { useLanguage } from "../../context/LanguageContext";
import { getT } from "../../translations";

gsap.registerPlugin(ScrollTrigger);

const VIDEOS = [
  "https://res.cloudinary.com/dep7dpjup/video/upload/v1772596741/1f4469285b97eae371f0be065ecf0243_siesjh.mp4",
  "https://res.cloudinary.com/dep7dpjup/video/upload/v1772596738/c9af2b316a0ba78930ba61ffa1a65c04_qagoks.mp4",
  "https://res.cloudinary.com/dep7dpjup/video/upload/v1772596737/16100955dd36845cba6abe90f7122170_u350t6.mp4",
  "https://res.cloudinary.com/dep7dpjup/video/upload/v1772596735/d6d38f98c4053e608b299906b03ec1b1_t4_df8gpk.mp4",
  "https://res.cloudinary.com/dep7dpjup/video/upload/v1772596734/00cc6afcd52ac41a63c01ccda9e7ffa4_mumebi.mp4",
  "https://res.cloudinary.com/dep7dpjup/video/upload/v1772596731/03291343a120ce05562eaac263e1734a_ina3dx.mp4",
  "https://res.cloudinary.com/dep7dpjup/video/upload/v1772596730/86d27e077cdcd1f50d109628874d6fc3_izsegu.mp4",
  "https://res.cloudinary.com/dep7dpjup/video/upload/v1772596719/b419f31af01c50e8bff9c5ad963462c6_yo8flh.mp4",
  "https://res.cloudinary.com/dep7dpjup/video/upload/v1772596717/6fc518ec60a47500c8b122f1cf676f15_ol1uut.mp4",
  "https://res.cloudinary.com/dep7dpjup/video/upload/v1772596715/f72b2b79e07f21f170fc9da406333857_uqmrmz.mp4",
];

function VideoGallery() {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const gridRef = useRef(null);
  const { lang } = useLanguage();
  const t = getT(lang);
  const [activeVideo, setActiveVideo] = useState(null);
  const videoRefs = useRef([]);

  useEffect(() => {
    if (activeVideo === null) return;
    const refs = videoRefs.current;
    const prev = refs[activeVideo];
    if (prev) prev.play().catch(() => {});
    return () => {
      refs.forEach((el) => el && el.pause());
    };
  }, [activeVideo]);

  useEffect(() => {
    const section = sectionRef.current;
    const header = headerRef.current;
    const grid = gridRef.current;
    if (!section || !header) return;
    const cards = grid ? grid.querySelectorAll(".video-gallery-card") : [];
    gsap.set([header, ...cards], { opacity: 0, y: 28 });
    const tl = gsap.timeline({
      scrollTrigger: { trigger: section, start: "top 80%", toggleActions: "play none none none" },
    });
    tl.to(header, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" })
      .to(cards, { opacity: 1, y: 0, duration: 0.4, stagger: 0.06, ease: "power2.out" }, "-=0.2");
    return () => ScrollTrigger.getAll().forEach((t) => t.kill());
  }, []);

  const handleVideoClick = (index) => {
    const next = activeVideo === index ? null : index;
    if (activeVideo !== null && videoRefs.current[activeVideo]) {
      videoRefs.current[activeVideo].pause();
      videoRefs.current[activeVideo].currentTime = 0;
    }
    setActiveVideo(next);
  };

  return (
    <section id="video-gallery" ref={sectionRef} className="video-gallery-section">
      <div className="video-gallery-container">
        <div ref={headerRef} className="video-gallery-header">
          <span className="video-gallery-bubbles">
            <span className="bubble" />
            <span className="bubble" />
            <span className="bubble" />
          </span>
          <h2 className="video-gallery-title">{t("videoGallery.title")}</h2>
          <p className="video-gallery-subtitle">{t("videoGallery.subtitle")}</p>
          <span className="video-gallery-bubbles">
            <span className="bubble" />
            <span className="bubble" />
            <span className="bubble" />
          </span>
        </div>

        <div ref={gridRef} className="video-gallery-grid">
          {VIDEOS.map((src, index) => (
            <div
              key={index}
              className={`video-gallery-card ${activeVideo === index ? "active" : ""}`}
              onClick={() => handleVideoClick(index)}
            >
              <div className="video-card-inner">
                <video
                  ref={(el) => (videoRefs.current[index] = el)}
                  src={src}
                  className="gallery-video"
                  controls={activeVideo === index}
                  muted={activeVideo !== index}
                  loop
                  playsInline
                  onMouseEnter={(e) => !activeVideo && e.target.play()}
                  onMouseLeave={(e) => {
                    if (activeVideo !== index) {
                      e.target.pause();
                      e.target.currentTime = 0;
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                {activeVideo !== index && (
                  <div className="video-play-overlay">
                    <span className="play-icon">▶</span>
                  </div>
                )}
                <div className="video-card-number">{index + 1}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default VideoGallery;
